import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getBanners,
  createBanner,
  updateBanner,
  deleteBanner,
} from "../../feature/banner/bannerThunks";
import {
  selectBannerList,
  selectBannerLoading,
  selectBannerCreateLoading,
  selectBannerCreateSuccess,
  selectBannerUpdateLoading,
  selectBannerUpdateSuccess,
  selectBannerDeleteLoading,
  selectBannerDeleteSuccess,
} from "../../feature/banner/bannerSelector";
import { resetBannerStatus } from "../../feature/banner/bannerSlice";

import {
  Box,
  Paper,
  Stack,
  Typography,
  IconButton,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Pagination,
  CircularProgress,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Switch,
  Tooltip,
  Button,
  FormControlLabel,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ImageIcon from "@mui/icons-material/Image";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";

import CommonButton from "../../components/commonComponents/CommonButton";
import CommonToast from "../../components/commonComponents/Toster";
import CommonSearchField from "../../components/commonComponents/CommonSearchField";
import CommonPagination from "../../components/commonComponents/CommonPagination";

const API_BASE_URL = "https://apidata.hogonnindia.com";

const getFullImageUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${API_BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
};

const Banner = () => {
  const dispatch = useDispatch();

  const rawBanners = useSelector(selectBannerList);
  const loading = useSelector(selectBannerLoading);
  const createLoading = useSelector(selectBannerCreateLoading);
  const createSuccess = useSelector(selectBannerCreateSuccess);
  const updateLoading = useSelector(selectBannerUpdateLoading);
  const updateSuccess = useSelector(selectBannerUpdateSuccess);
  const deleteLoading = useSelector(selectBannerDeleteLoading);
  const deleteSuccess = useSelector(selectBannerDeleteSuccess);

  // Normalize list
  const bannerList = useMemo(() => {
    if (Array.isArray(rawBanners)) return rawBanners;
    if (Array.isArray(rawBanners?.data)) return rawBanners.data;
    return [];
  }, [rawBanners]);

  // Dialog & Modal States
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewBanner, setViewBanner] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState(null);

  // Status toggle in-flight tracking (by ID)
  const [togglingId, setTogglingId] = useState(null);

  // Search & Filter & Pagination
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All"); // All | Active | Inactive
  const [page, setPage] = useState(1);
  const itemsPerPage = 8;

  // Form State
  const initialFormState = {
    title: "",
    subtitle: "",
    status: true,
    image: null,
  };
  const [form, setForm] = useState(initialFormState);
  const [existingImageUrl, setExistingImageUrl] = useState("");
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [errors, setErrors] = useState({});

  // Fetch banners
  const fetchAllBanners = () => {
    dispatch(getBanners());
  };

  useEffect(() => {
    fetchAllBanners();
  }, [dispatch]);

  // Handle Redux success events
  useEffect(() => {
    if (createSuccess) {
      CommonToast("Banner created successfully!", "success");
      dispatch(resetBannerStatus());
      handleCloseFormDialog();
      fetchAllBanners();
    }
    if (updateSuccess) {
      // If updating from form dialog
      if (formDialogOpen) {
        CommonToast("Banner updated successfully!", "success");
        handleCloseFormDialog();
      }
      dispatch(resetBannerStatus());
      fetchAllBanners();
      setTogglingId(null);
    }
    if (deleteSuccess) {
      CommonToast("Banner deleted successfully!", "success");
      dispatch(resetBannerStatus());
      setDeleteDialogOpen(false);
      setBannerToDelete(null);
      fetchAllBanners();
    }
  }, [createSuccess, updateSuccess, deleteSuccess, formDialogOpen, dispatch]);

  // Filtering
  const filteredBanners = useMemo(() => {
    return bannerList.filter((item) => {
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        item.title?.toLowerCase().includes(q) ||
        item.subtitle?.toLowerCase().includes(q) ||
        String(item.id).includes(q);

      const matchesStatus =
        statusFilter === "All" ||
        (statusFilter === "Active" && item.status === true) ||
        (statusFilter === "Inactive" && item.status === false);

      return matchesSearch && matchesStatus;
    });
  }, [bannerList, searchQuery, statusFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredBanners.length / itemsPerPage) || 1;
  const paginatedBanners = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return filteredBanners.slice(start, start + itemsPerPage);
  }, [filteredBanners, page, itemsPerPage]);

  // Stats
  const stats = useMemo(() => {
    const total = bannerList.length;
    const active = bannerList.filter((b) => b.status === true).length;
    const inactive = total - active;
    return { total, active, inactive };
  }, [bannerList]);

  // Form Handlers
  const handleOpenAddDialog = () => {
    setEditId(null);
    setForm(initialFormState);
    setExistingImageUrl("");
    setPreviewImageUrl(null);
    setErrors({});
    setFormDialogOpen(true);
  };

  const handleOpenEditDialog = (item) => {
    setEditId(item.id);
    setForm({
      title: item.title || "",
      subtitle: item.subtitle || "",
      status: item.status ?? true,
      image: null,
    });
    setExistingImageUrl(item.image ? getFullImageUrl(item.image) : "");
    setPreviewImageUrl(null);
    setErrors({});
    setFormDialogOpen(true);
  };

  const handleCloseFormDialog = () => {
    setFormDialogOpen(false);
    setEditId(null);
    setForm(initialFormState);
    setExistingImageUrl("");
    setPreviewImageUrl(null);
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setErrors((prev) => ({
          ...prev,
          image: "Please upload an image file (PNG, JPG, WebP)",
        }));
        return;
      }
      setForm((prev) => ({ ...prev, image: file }));
      setPreviewImageUrl(URL.createObjectURL(file));
      if (errors.image) {
        setErrors((prev) => ({ ...prev, image: "" }));
      }
    }
  };

  const handleRemoveSelectedImage = () => {
    setForm((prev) => ({ ...prev, image: null }));
    setPreviewImageUrl(null);
  };

  const validateForm = () => {
    const errs = {};
    if (!form.title.trim()) {
      errs.title = "Banner title is required";
    }
    if (!editId && !form.image) {
      errs.image = "Banner image is required";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmitForm = () => {
    if (!validateForm()) return;

    const data = new FormData();
    data.append("title", form.title.trim());
    if (form.subtitle !== undefined) {
      data.append("subtitle", form.subtitle.trim());
    }
    data.append("status", String(form.status));
    if (form.image) {
      data.append("image", form.image);
    }

    if (editId) {
      dispatch(updateBanner({ id: editId, data }))
        .unwrap()
        .then(() => {
          // Handled in useEffect
        })
        .catch((err) => {
          const msg =
            typeof err === "object"
              ? Object.values(err).flat().join(", ")
              : String(err);
          CommonToast(msg || "Failed to update banner", "error");
        });
    } else {
      dispatch(createBanner(data))
        .unwrap()
        .then(() => {
          // Handled in useEffect
        })
        .catch((err) => {
          const msg =
            typeof err === "object"
              ? Object.values(err).flat().join(", ")
              : String(err);
          CommonToast(msg || "Failed to create banner", "error");
        });
    }
  };

  // Status toggle handler
  const handleToggleStatus = (item) => {
    setTogglingId(item.id);
    const newStatus = !item.status;
    const data = new FormData();
    data.append("status", String(newStatus));

    dispatch(updateBanner({ id: item.id, data }))
      .unwrap()
      .then(() => {
        CommonToast(
          `Banner ${newStatus ? "activated" : "deactivated"} successfully`,
          "success",
        );
        fetchAllBanners();
      })
      .catch(() => {
        CommonToast("Failed to update status", "error");
      })
      .finally(() => {
        setTogglingId(null);
      });
  };

  // View handler
  const handleOpenView = (item) => {
    setViewBanner(item);
    setViewDialogOpen(true);
  };

  // Delete handlers
  const handleOpenDelete = (item) => {
    setBannerToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!bannerToDelete?.id) return;
    dispatch(deleteBanner(bannerToDelete.id))
      .unwrap()
      .catch((err) => {
        CommonToast(err || "Failed to delete banner", "error");
      });
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {/* 1. TOP HEADER & ACTIONS */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: "#1a1d27", letterSpacing: "-0.5px" }}
          >
            Banner Management
          </Typography>
          <Typography variant="body2" sx={{ color: "#667085", mt: 0.5 }}>
            Create and manage homepage banners, sliders, and promotions
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title="Refresh Banner List">
            <IconButton
              onClick={fetchAllBanners}
              disabled={loading}
              sx={{
                border: "1px solid #e2e5ef",
                borderRadius: "8px",
                p: 1.1,
                bgcolor: "#fff",
                "&:hover": { bgcolor: "#f8f9fc" },
              }}
            >
              <RefreshIcon
                fontSize="small"
                sx={{
                  animation: loading ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
            </IconButton>
          </Tooltip>

          <CommonButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
          >
            Add New Banner
          </CommonButton>
        </Stack>
      </Stack>

      {/* 2. STATS PILLS & QUICK FILTERS */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={4}>
          <Paper
            elevation={0}
            onClick={() => setStatusFilter("All")}
            sx={{
              p: 2,
              borderRadius: "12px",
              border: `1.5px solid ${statusFilter === "All" ? "#D20000" : "#eaecf0"}`,
              bgcolor: statusFilter === "All" ? "rgba(210,0,0,0.02)" : "#fff",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": { borderColor: "#D20000" },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "10px",
                  bgcolor: "rgba(210,0,0,0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#D20000",
                }}
              >
                <ViewCarouselIcon />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: "#667085", fontWeight: 500 }}>
                  Total Banners
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1d27" }}>
                  {stats.total}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper
            elevation={0}
            onClick={() => setStatusFilter("Active")}
            sx={{
              p: 2,
              borderRadius: "12px",
              border: `1.5px solid ${statusFilter === "Active" ? "#12b76a" : "#eaecf0"}`,
              bgcolor: statusFilter === "Active" ? "rgba(18,183,106,0.03)" : "#fff",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": { borderColor: "#12b76a" },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "10px",
                  bgcolor: "rgba(18,183,106,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#12b76a",
                }}
              >
                <CheckCircleIcon />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: "#667085", fontWeight: 500 }}>
                  Active Banners
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1d27" }}>
                  {stats.active}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper
            elevation={0}
            onClick={() => setStatusFilter("Inactive")}
            sx={{
              p: 2,
              borderRadius: "12px",
              border: `1.5px solid ${statusFilter === "Inactive" ? "#7E7E7E" : "#eaecf0"}`,
              bgcolor: statusFilter === "Inactive" ? "rgba(126,126,126,0.04)" : "#fff",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": { borderColor: "#7E7E7E" },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "10px",
                  bgcolor: "rgba(126,126,126,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#7E7E7E",
                }}
              >
                <CancelIcon />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: "#667085", fontWeight: 500 }}>
                  Inactive Banners
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1d27" }}>
                  {stats.inactive}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* 3. SEARCH & CONTROLS BAR */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2.5,
          borderRadius: "12px",
          border: "1px solid #eaecf0",
          bgcolor: "#fff",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Box sx={{ width: { xs: "100%", sm: 360 } }}>
            <CommonSearchField
              value={searchQuery}
              placeholder="Search by title or subtitle..."
              onChange={(value) => {
                setSearchQuery(value);
                setPage(1);
              }}
            />
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            {["All", "Active", "Inactive"].map((tab) => (
              <Chip
                key={tab}
                label={tab}
                clickable
                onClick={() => {
                  setStatusFilter(tab);
                  setPage(1);
                }}
                sx={{
                  fontWeight: 600,
                  fontSize: "13px",
                  borderRadius: "8px",
                  bgcolor: statusFilter === tab ? "#050622" : "#f2f4f7",
                  color: statusFilter === tab ? "#fff" : "#475467",
                  "&:hover": {
                    bgcolor: statusFilter === tab ? "#050622" : "#e4e7ec",
                  },
                }}
              />
            ))}
          </Stack>
        </Stack>
      </Paper>

      {/* 4. MAIN BANNER TABLE */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: "1px solid #eaecf0",
          overflow: "hidden",
        }}
      >
        <Table sx={{ minWidth: 650 }}>
          <TableHead sx={{ bgcolor: "#f8f9fc" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: "#475467", width: 60 }}>
                #
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#475467", width: 140 }}>
                Banner
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#475467" }}>
                Title
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#475467" }}>
                Subtitle
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#475467", width: 150 }}>
                Status
              </TableCell>
              <TableCell
                sx={{ fontWeight: 700, color: "#475467", width: 130 }}
                align="center"
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} sx={{ color: "#D20000" }} />
                  <Typography variant="body2" sx={{ color: "#667085", mt: 1.5 }}>
                    Loading banner slides...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : paginatedBanners.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      bgcolor: "#f2f4f7",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 1.5,
                      color: "#98a2b3",
                    }}
                  >
                    <ImageIcon fontSize="large" />
                  </Box>
                  <Typography variant="subtitle1" fontWeight={600} color="#1a1d27">
                    No banners found
                  </Typography>
                  <Typography variant="body2" color="#667085" mb={2}>
                    {searchQuery
                      ? "Try adjusting your search terms or filters."
                      : "Get started by adding your very first promotional banner."}
                  </Typography>
                  {!searchQuery && (
                    <CommonButton
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleOpenAddDialog}
                    >
                      Add Banner
                    </CommonButton>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              paginatedBanners.map((banner, index) => {
                const srNo = (page - 1) * itemsPerPage + index + 1;
                const imgUrl = getFullImageUrl(banner.image);
                const isToggling = togglingId === banner.id;

                return (
                  <TableRow
                    key={banner.id}
                    hover
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      transition: "background-color 0.15s ease",
                    }}
                  >
                    {/* Index */}
                    <TableCell sx={{ color: "#667085", fontWeight: 500 }}>
                      {srNo}
                    </TableCell>

                    {/* Banner Image Preview */}
                    <TableCell>
                      <Box
                        onClick={() => handleOpenView(banner)}
                        sx={{
                          width: 100,
                          height: 52,
                          borderRadius: "8px",
                          overflow: "hidden",
                          bgcolor: "#f2f4f7",
                          cursor: "pointer",
                          border: "1px solid #eaecf0",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          position: "relative",
                          transition: "transform 0.2s ease, box-shadow 0.2s ease",
                          "&:hover": {
                            transform: "scale(1.05)",
                            boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                          },
                        }}
                      >
                        {imgUrl ? (
                          <img
                            src={imgUrl}
                            alt={banner.title || "Banner"}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        ) : (
                          <ImageIcon sx={{ color: "#98a2b3" }} />
                        )}
                      </Box>
                    </TableCell>

                    {/* Title */}
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "#1a1d27",
                          lineHeight: 1.4,
                        }}
                      >
                        {banner.title || "Untitled Banner"}
                      </Typography>
                    </TableCell>

                    {/* Subtitle */}
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          color: banner.subtitle ? "#475467" : "#98a2b3",
                          fontStyle: banner.subtitle ? "normal" : "italic",
                        }}
                      >
                        {banner.subtitle || "—"}
                      </Typography>
                    </TableCell>

                    {/* Status Toggle */}
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {isToggling ? (
                          <CircularProgress size={20} sx={{ color: "#D20000" }} />
                        ) : (
                          <Switch
                            checked={Boolean(banner.status)}
                            onChange={() => handleToggleStatus(banner)}
                            size="small"
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": {
                                color: "#12b76a",
                              },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                backgroundColor: "#12b76a",
                              },
                            }}
                          />
                        )}
                        <Chip
                          label={banner.status ? "Active" : "Inactive"}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: "11px",
                            fontWeight: 600,
                            borderRadius: "6px",
                            bgcolor: banner.status ? "#ecfdf3" : "#f2f4f7",
                            color: banner.status ? "#027a48" : "#475467",
                            border: `1px solid ${banner.status ? "#a6f4c5" : "#e4e7ec"}`,
                          }}
                        />
                      </Stack>
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="center">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="center"
                      >
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenView(banner)}
                            sx={{
                              color: "#475467",
                              "&:hover": { bgcolor: "#f2f4f7", color: "#1a1d27" },
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Edit Banner">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEditDialog(banner)}
                            sx={{
                              color: "#027a48",
                              "&:hover": { bgcolor: "#ecfdf3" },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete Banner">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDelete(banner)}
                            sx={{
                              color: "#D20000",
                              "&:hover": { bgcolor: "rgba(210,0,0,0.08)" },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Footer */}
      {!loading && filteredBanners.length > 0 && (
        <CommonPagination
          count={totalPages}
          page={page}
          onPageChange={setPage}
          totalItems={filteredBanners.length}
          rowsPerPage={itemsPerPage}
          itemName="banners"
        />
      )}

      {/* 5. CREATE / EDIT BANNER DIALOG */}
      <Dialog
        open={formDialogOpen}
        onClose={handleCloseFormDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            p: 1,
            boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1d27" }}>
            {editId ? "Edit Banner" : "Create New Banner"}
          </Typography>
          <IconButton onClick={handleCloseFormDialog} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2.5 }}>
          <Stack spacing={2.5}>
            {/* Title */}
            <TextField
              label="Banner Title *"
              name="title"
              placeholder="e.g. Paint Protection Film - Made in India"
              value={form.title}
              onChange={handleInputChange}
              error={Boolean(errors.title)}
              helperText={errors.title}
              fullWidth
              variant="outlined"
            />

            {/* Subtitle */}
            <TextField
              label="Subtitle (Optional)"
              name="subtitle"
              placeholder="e.g. Ultimate Shield for Your Vehicle"
              value={form.subtitle}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
            />

            {/* Status switch */}
            <Box
              sx={{
                p: 1.5,
                borderRadius: "10px",
                border: "1px solid #eaecf0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#1a1d27" }}>
                  Active Status
                </Typography>
                <Typography variant="caption" sx={{ color: "#667085" }}>
                  Active banners appear on the live storefront carousel
                </Typography>
              </Box>
              <Switch
                checked={form.status}
                name="status"
                onChange={handleInputChange}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#12b76a",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#12b76a",
                  },
                }}
              />
            </Box>

            {/* Image Upload Area */}
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#344054", mb: 1 }}
              >
                Banner Image {!editId && "*"}
              </Typography>

              {/* Current or Preview Image */}
              {(previewImageUrl || existingImageUrl) && (
                <Box
                  sx={{
                    mb: 1.5,
                    p: 1,
                    borderRadius: "10px",
                    border: "1px solid #eaecf0",
                    position: "relative",
                    bgcolor: "#f8f9fc",
                  }}
                >
                  <img
                    src={previewImageUrl || existingImageUrl}
                    alt="Preview"
                    style={{
                      width: "100%",
                      maxHeight: "180px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      display: "block",
                    }}
                  />
                  {previewImageUrl && (
                    <IconButton
                      onClick={handleRemoveSelectedImage}
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 14,
                        right: 14,
                        bgcolor: "rgba(0,0,0,0.6)",
                        color: "#fff",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                  <Typography
                    variant="caption"
                    sx={{ color: "#667085", mt: 0.5, display: "block" }}
                  >
                    {previewImageUrl
                      ? `New selected file: ${form.image?.name}`
                      : "Current banner image"}
                  </Typography>
                </Box>
              )}

              {/* Upload Dropzone */}
              <Box
                component="label"
                sx={{
                  border: `2px dashed ${errors.image ? "#D20000" : "#d0d5dd"}`,
                  borderRadius: "12px",
                  p: 3,
                  textAlign: "center",
                  cursor: "pointer",
                  display: "block",
                  bgcolor: "#fafafa",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "#D20000",
                    bgcolor: "rgba(210,0,0,0.02)",
                  },
                }}
              >
                <input
                  type="file"
                  accept="image/png, image/jpeg, image/webp"
                  hidden
                  onChange={handleImageChange}
                />
                <CloudUploadIcon sx={{ fontSize: 36, color: "#D20000", mb: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#1a1d27" }}>
                  {previewImageUrl || existingImageUrl
                    ? "Click to choose a different image"
                    : "Click or drag to upload banner image"}
                </Typography>
                <Typography variant="caption" sx={{ color: "#667085" }}>
                  Supports PNG, JPG, or WebP (Recommended 1920x800 or similar 16:9 ratio)
                </Typography>
              </Box>

              {errors.image && (
                <Typography variant="caption" sx={{ color: "#D20000", mt: 0.5, display: "block" }}>
                  {errors.image}
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseFormDialog}
            sx={{ color: "#667085", textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <CommonButton
            variant="contained"
            onClick={handleSubmitForm}
            disabled={createLoading || updateLoading}
          >
            {createLoading || updateLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} sx={{ color: "#fff" }} />
                <span>Saving...</span>
              </Stack>
            ) : editId ? (
              "Save Changes"
            ) : (
              "Create Banner"
            )}
          </CommonButton>
        </DialogActions>
      </Dialog>

      {/* 6. VIEW BANNER DETAILS DIALOG */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            p: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1d27" }}>
            Banner Preview
          </Typography>
          <IconButton onClick={() => setViewDialogOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2.5 }}>
          {viewBanner && (
            <Stack spacing={2.5}>
              {/* Image Hero Preview */}
              <Box
                sx={{
                  borderRadius: "12px",
                  overflow: "hidden",
                  border: "1px solid #eaecf0",
                  bgcolor: "#f2f4f7",
                  maxHeight: "360px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {viewBanner.image ? (
                  <img
                    src={getFullImageUrl(viewBanner.image)}
                    alt={viewBanner.title}
                    style={{
                      width: "100%",
                      height: "auto",
                      maxHeight: "360px",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <Box sx={{ p: 4, textAlign: "center", color: "#98a2b3" }}>
                    <ImageIcon sx={{ fontSize: 48, mb: 1 }} />
                    <Typography variant="body2">No image attached</Typography>
                  </Box>
                )}
              </Box>

              {/* Banner Details */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <Typography variant="caption" sx={{ color: "#667085", fontWeight: 600 }}>
                    TITLE
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1d27", mb: 1.5 }}>
                    {viewBanner.title || "Untitled"}
                  </Typography>

                  <Typography variant="caption" sx={{ color: "#667085", fontWeight: 600 }}>
                    SUBTITLE
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#475467" }}>
                    {viewBanner.subtitle || "None"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "#f8f9fc",
                      borderRadius: "10px",
                      border: "1px solid #eaecf0",
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "#667085", fontWeight: 600 }}>
                      BANNER ID
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#1a1d27", mb: 1.5 }}>
                      #{viewBanner.id}
                    </Typography>

                    <Typography variant="caption" sx={{ color: "#667085", fontWeight: 600 }}>
                      STATUS
                    </Typography>
                    <Box mt={0.5}>
                      <Chip
                        label={viewBanner.status ? "Active" : "Inactive"}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          bgcolor: viewBanner.status ? "#ecfdf3" : "#f2f4f7",
                          color: viewBanner.status ? "#027a48" : "#475467",
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Stack>
          )}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setViewDialogOpen(false)}
            sx={{ color: "#667085", textTransform: "none", fontWeight: 600 }}
          >
            Close
          </Button>
          <CommonButton
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => {
              setViewDialogOpen(false);
              handleOpenEditDialog(viewBanner);
            }}
          >
            Edit Banner
          </CommonButton>
        </DialogActions>
      </Dialog>

      {/* 7. DELETE CONFIRMATION DIALOG */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "16px", p: 1 },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                bgcolor: "rgba(210,0,0,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#D20000",
              }}
            >
              <WarningAmberIcon />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1d27" }}>
              Delete Banner?
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: "#475467", lineHeight: 1.6 }}>
            Are you sure you want to permanently delete{" "}
            <strong>"{bannerToDelete?.title}"</strong>? This banner will be removed from your website and cannot be recovered.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleteLoading}
            sx={{ color: "#667085", textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleteLoading}
            sx={{
              bgcolor: "#D20000",
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              boxShadow: "none",
              "&:hover": { bgcolor: "#B80000" },
            }}
          >
            {deleteLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} sx={{ color: "#fff" }} />
                <span>Deleting...</span>
              </Stack>
            ) : (
              "Yes, Delete Banner"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Banner;
